/**
 * Carries the backend 401 `reason` across a full-page sign-out so `/sign-in`
 * can explain what happened. Uses `sessionStorage` because in-memory state
 * would not survive the navigation.
 *
 * Keep `AUTH_ERROR_REASON` in sync with `AuthErrorReason` in
 * `backend/src/app/identity/auth.py` and `MobileApp/constants/authErrors.ts`.
 */

/** Mirror of backend `AuthErrorReason`. Rename in all three places. */
export const AUTH_ERROR_REASON = {
  MISSING_CREDENTIALS: 'missing_credentials',
  TOKEN_EXPIRED: 'token_expired',
  TOKEN_REVOKED: 'token_revoked',
  TOKEN_INVALID: 'token_invalid',
  USER_DISABLED: 'user_disabled',
  USER_NOT_FOUND: 'user_not_found',
  MISSING_CLAIMS: 'missing_claims',
} as const;

export type AuthErrorReason =
  (typeof AUTH_ERROR_REASON)[keyof typeof AUTH_ERROR_REASON];

/**
 * `sessionStorage`, not a router state object: the sign-out does a full page
 * navigation so every cache and hook belonging to the dead session is dropped,
 * and in-memory state would not survive that. Scoped to the tab, so a forced
 * sign-out in one tab never explains itself in an unrelated one.
 */
const NOTICE_KEY = 'pml.signOutReason';

/** Human copy for the sign-in banner. Defaults to the vaguest safe wording. */
export function signOutNoticeFor(reason: string | null | undefined): string {
  switch (reason) {
    case AUTH_ERROR_REASON.TOKEN_REVOKED:
      return "Your password was changed, so you've been signed out on this device. Please sign in again.";
    case AUTH_ERROR_REASON.USER_DISABLED:
      return 'This account has been disabled. Please contact hello@pineapplemusiclab.com.';
    case AUTH_ERROR_REASON.USER_NOT_FOUND:
      return 'This account no longer exists. Please sign in again.';
    default:
      return 'Your session expired. Please sign in again to pick up where you left off.';
  }
}

export function stashSignOutNotice(reason: string | null | undefined): void {
  try {
    // Store the reason, not the copy, so the wording can change without
    // stranding a message written by an older bundle.
    sessionStorage.setItem(NOTICE_KEY, reason ?? '');
  } catch {
    // Private-mode or storage-full: losing the banner is not worth failing the
    // sign-out over.
  }
}

/**
 * Memoized for the lifetime of the page load. `undefined` means "not read yet",
 * which is distinct from a read that found nothing.
 */
let taken: string | null | undefined;

/**
 * Read the pending notice and clear it, so it shows exactly once. Returns
 * ``null`` when this sign-in was not the result of a forced sign-out.
 *
 * Idempotent within a page load: clearing storage on the first call would
 * otherwise let a remount — or a double-invoked state initializer under
 * `StrictMode` — consume the notice out from under the render about to show it.
 * The storage entry is still removed immediately, so a later page load is clean.
 */
export function takeSignOutNotice(): string | null {
  if (taken !== undefined) return taken;
  try {
    const reason = sessionStorage.getItem(NOTICE_KEY);
    taken = reason === null ? null : signOutNoticeFor(reason);
    if (reason !== null) sessionStorage.removeItem(NOTICE_KEY);
  } catch {
    taken = null;
  }
  return taken;
}
